import { Play, RefreshCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { GetSyncerStatusDocument, RestartSyncerDocument, StartSyncerDocument } from "@/gen/graphql";

export const SyncerControl = () => {
    const [isLoading, setIsLoading] = useState(false);

    const [{ data, fetching, error }, refetch] = useQuery({
        query: GetSyncerStatusDocument,
        requestPolicy: "network-only",
    });

    const [_startResult, startSyncer] = useMutation(StartSyncerDocument);
    const [_restartResult, restartSyncer] = useMutation(RestartSyncerDocument);

    const syncerStatus = data?.syncerStatus;

    const getStatusDotColor = (state: string | undefined) => {
        if (!state) return "bg-gray-400";
        switch (state.toLowerCase()) {
            case "started":
            case "running":
                return "bg-green-500";
            case "stopped":
            case "stopping":
                return "bg-red-500";
            case "starting":
                return "bg-yellow-500";
            default:
                return "bg-gray-400";
        }
    };

    const handleStart = async () => {
        setIsLoading(true);
        try {
            const result = await startSyncer({});
            if (result.error) {
                toast.error("Failed to start syncer", {
                    description: result.error.message,
                });
            } else {
                toast.success("Syncer started successfully");
                setTimeout(() => refetch(), 2000);
            }
        } catch (err) {
            toast.error("Failed to start syncer");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestart = async () => {
        const confirmed = window.confirm(
            "Are you sure you want to restart syncer? It's already running, so normally there should be no need to."
        );
        if (!confirmed) return;

        setIsLoading(true);
        try {
            const result = await restartSyncer({});
            if (result.error) {
                toast.error("Failed to restart syncer", {
                    description: result.error.message,
                });
            } else {
                toast.success("Syncer restarted successfully");
                setTimeout(() => refetch(), 2000);
            }
        } catch (err) {
            toast.error("Failed to restart syncer");
        } finally {
            setIsLoading(false);
        }
    };

    const isRunning =
        syncerStatus?.state?.toLowerCase() === "started" ||
        syncerStatus?.state?.toLowerCase() === "running";

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-lg">Syncer control</CardTitle>
                        <CardDescription>
                            Controls the service that powers all live tournament broadcasts. This
                            should <b>always</b> be running, otherwise no live broadcasts will work!
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {error ? (
                    <div className="text-destructive text-sm">
                        {error.message.includes("Fly API client not configured")
                            ? "Fly API is not configured. This feature is only available in production."
                            : `Error: ${error.message}`}
                    </div>
                ) : !syncerStatus ? (
                    <div className="text-muted-foreground text-sm">
                        {fetching ? "Loading syncer status..." : "No syncer machine found"}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <div
                                        className={`h-3 w-3 rounded-full ${getStatusDotColor(syncerStatus.state)}`}
                                    />
                                    {syncerStatus.state?.toLowerCase() === "started" && (
                                        <div
                                            className={`absolute inset-0 h-3 w-3 rounded-full ${getStatusDotColor(syncerStatus.state)} animate-ping`}
                                        />
                                    )}
                                </div>
                                <span className="text-sm font-medium">
                                    {syncerStatus.state?.toLowerCase() === "started"
                                        ? "Running"
                                        : syncerStatus.state?.toLowerCase() === "stopped"
                                          ? "Stopped"
                                          : syncerStatus.state?.toLowerCase() === "stopping"
                                            ? "Stopping"
                                            : syncerStatus.state?.toLowerCase() === "starting"
                                              ? "Starting"
                                              : "Unknown"}
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                onClick={handleStart}
                                disabled={isLoading || isRunning || fetching}
                                size="sm"
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                <Play className="h-4 w-4" />
                                Start
                            </Button>
                            <Button
                                onClick={handleRestart}
                                disabled={isLoading || !isRunning || fetching}
                                size="sm"
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                <RefreshCcw className="h-4 w-4" />
                                Restart
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
